/**
 * OsStatusComponent (PRD-004 §4 PR-OS-02; final design pass StatusSpine).
 *
 * The owner-facing status surface for an OS engagement. Matches the Claude
 * Design StatusSpine (design-final/Studio OS.html): the Eric avatar + signature
 * row, the big serif-italic status line, the calm supporting paragraph, the
 * optional per-stage booking CTA, then the four-phase PhaseRail spine
 * (Session one -> Build -> Session two -> Open).
 *
 * Deliberately NOT the 72h ClockBar and NOT the task kanban (scoping pass §9):
 * no countdown, no `due_at`, no deadline-pressure semantics. The OS surface
 * never labels the engagement a "task" or a "tier" (OS-08 product legibility).
 * It is a warm, owner-facing surface and uses the portal's warm-brand tokens
 * (packages/design-tokens), unlike the operator surface.
 *
 * CTA wiring (final design pass, migration 0032): at `scoped` we render a
 * "Book session one" button linking to `engagement.session1_booking_url`, and
 * ONLY when that operator-pasted URL is present. At `handoff_ready` we render a
 * "Book the install" button linking to `engagement.session2_booking_url` when
 * present (DELIVERY.md intent: the owner books the install after the
 * walkthrough). Both are real external `<a href>` booking links (out-of-band,
 * TECH-OS-04), not router links. No countdown / deadline anywhere.
 *
 * Read-only otherwise: there is no owner control that mutates status. The
 * lifecycle is operator-driven (scoping pass §8: no owner INSERT/UPDATE policy).
 */

import type { OsStatus, OwnOsEngagement } from '@/lib/os';
import { osStatusCopy } from './os-status-copy';

/**
 * The four owner-legible phases of the two-session spine. Each `os_status`
 * maps onto one. `Open` is the terminal "studio's open" phase past install.
 */
const PHASES: ReadonlyArray<string> = [
  'Session one',
  'Build',
  'Session two',
  'Open',
];

/**
 * The spine phase index an `os_status` sits in. `intake` / `acknowledged` /
 * `scoped` are the lead-in to Session one; `session1_booked` reaches it.
 * `discovery_done` / `building` / `handoff_ready` are the build window.
 * `session2_booked` / `installed` reach Session two (install). `open` is the
 * terminal phase.
 */
function phaseIndexForStatus(status: OsStatus): number {
  switch (status) {
    case 'intake':
    case 'acknowledged':
    case 'scoped':
    case 'session1_booked':
      return 0;
    case 'discovery_done':
    case 'building':
    case 'handoff_ready':
      return 1;
    case 'session2_booked':
    case 'installed':
      return 2;
    case 'open':
      return 3;
    default: {
      const _exhaustive: never = status;
      return _exhaustive;
    }
  }
}

/**
 * A calm, honest stamp under the signature row. Stage-driven (not a fabricated
 * relative time): each phase gets a short state label. No clock semantics.
 */
function statusStamp(status: OsStatus): string {
  switch (status) {
    case 'intake':
    case 'acknowledged':
      return 'on my desk';
    case 'scoped':
      return 'scoped';
    case 'session1_booked':
      return 'session one booked';
    case 'discovery_done':
      return 'in the build';
    case 'building':
      return 'in the build';
    case 'handoff_ready':
      return 'ready for you';
    case 'session2_booked':
      return 'install booked';
    case 'installed':
      return 'running on your machine';
    case 'open':
      return 'open';
    default: {
      const _exhaustive: never = status;
      return _exhaustive;
    }
  }
}

/** Eric avatar: navy circle with a serif-italic "E", matching the prototype. */
function EricAvatar() {
  return (
    <span
      className="flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-full bg-[color:var(--color-navy)] font-display italic text-sm text-[color:var(--color-linen)]"
      aria-hidden="true"
    >
      E
    </span>
  );
}

/**
 * The four-phase spine. Decorative (aria-hidden); the status line above carries
 * the accessible text status. Done phases get a filled navy node + check, the
 * current phase a navy ring with a navy dot, future phases a muted hollow dot.
 */
function PhaseRail({ current }: { current: number }) {
  return (
    <div className="flex max-w-[540px] items-start" aria-hidden="true">
      {PHASES.map((label, i) => {
        const done = i < current;
        const active = i === current;
        return (
          <div key={label} className="contents">
            <div className="flex w-24 shrink-0 flex-col items-center">
              <span
                className={[
                  'flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full',
                  done
                    ? 'border-[1.5px] border-[color:var(--color-navy)] bg-[color:var(--color-navy)]'
                    : active
                      ? 'border-2 border-[color:var(--color-navy)] bg-[color:var(--color-white)]'
                      : 'border-[1.5px] border-[color:var(--color-line-dark)] bg-[color:var(--color-white)]',
                ].join(' ')}
              >
                {done ? (
                  <span className="text-[11px] leading-none text-[color:var(--color-linen)]">
                    &#10003;
                  </span>
                ) : active ? (
                  <span className="h-2 w-2 rounded-full bg-[color:var(--color-navy)]" />
                ) : (
                  <span className="h-1.5 w-1.5 rounded-full bg-[color:var(--color-line-dark)]" />
                )}
              </span>
              <span
                className={[
                  'mt-2.5 text-center text-xs leading-tight',
                  done || active
                    ? 'font-medium text-[color:var(--color-black)]'
                    : 'text-[color:var(--color-text-mute)]',
                ].join(' ')}
              >
                {label}
              </span>
            </div>
            {i < PHASES.length - 1 ? (
              <span
                className={[
                  'mt-[10.5px] h-[1.5px] min-w-6 flex-1',
                  i < current
                    ? 'bg-[color:var(--color-navy)]'
                    : 'bg-[color:var(--color-line-dark)]',
                ].join(' ')}
              />
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

/**
 * The booking CTA href for the current stage, or null when no button shows.
 * Session one books at `scoped`; the install books at `handoff_ready`. Each
 * renders only when the operator has pasted the matching booking URL.
 */
function bookingHref(engagement: OwnOsEngagement): {
  href: string;
  label: string;
} | null {
  if (engagement.status === 'scoped' && engagement.session1_booking_url) {
    return { href: engagement.session1_booking_url, label: 'Book session one' };
  }
  if (
    engagement.status === 'handoff_ready' &&
    engagement.session2_booking_url
  ) {
    return { href: engagement.session2_booking_url, label: 'Book the install' };
  }
  return null;
}

export function OsStatusComponent({
  engagement,
}: {
  engagement: OwnOsEngagement;
}) {
  const copy = osStatusCopy(engagement);
  const current = phaseIndexForStatus(engagement.status);
  const stamp = statusStamp(engagement.status);
  const booking = bookingHref(engagement);

  return (
    <section aria-label="Your Operating System engagement">
      <div className="mb-[22px] flex items-center gap-2.5">
        <EricAvatar />
        <div className="leading-tight">
          <p className="text-[13px] font-semibold text-[color:var(--color-black)]">
            Eric, Yes Please Studio
          </p>
          <p className="text-xs text-[color:var(--color-text-mute)]">{stamp}</p>
        </div>
      </div>

      <h1 className="max-w-[760px] font-display italic text-[clamp(30px,4.4vw,50px)] leading-[1.08] tracking-[-0.01em] text-[color:var(--color-black)] text-pretty">
        {copy.line}
      </h1>
      <p className="mt-[18px] max-w-[540px] text-[clamp(15px,1.7vw,18px)] leading-relaxed text-[color:var(--color-gray-500)] text-pretty">
        {copy.sub}
      </p>

      {booking ? (
        <a
          href={booking.href}
          target="_blank"
          rel="noreferrer"
          className="mt-[22px] inline-flex items-center gap-1.5 rounded-lg bg-[color:var(--color-navy)] px-[22px] py-[11px] text-[15px] font-medium text-[color:var(--color-linen)] transition-colors hover:bg-[color:var(--color-navy-hover)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--color-navy)]"
        >
          {booking.label}
          <span aria-hidden="true">&rarr;</span>
        </a>
      ) : null}

      <div className="mt-[clamp(36px,5vw,56px)]">
        <PhaseRail current={current} />
      </div>
    </section>
  );
}
